// Catálogo "Imóveis da equipa" — só dados do imóvel (função imoveis_equipa no Supabase:
// nunca devolve proprietários, contactos, notas nem documentos).
// Cartões ou lista, filtros, ordenação por coluna e páginas de 20.
import { useState, useEffect, useMemo } from "react";

const POR_PAGINA = 20;
const ESTADOS = { rascunho: "Rascunho", pre_angariacao: "Pré-angariação", ativa: "Ativa", sob_proposta: "Sob proposta", cpcv: "CPCV", vendida: "Vendida", arrendada: "Arrendada", cancelada: "Cancelada" };
const COR_ESTADO = { rascunho: "#9ca3af", pre_angariacao: "#a855f7", ativa: "#3b82f6", sob_proposta: "#f59e0b", cpcv: "#7c3aed", vendida: "#10b981", arrendada: "#06b6d4", cancelada: "#ef4444" };
const FILTROS_VAZIOS = { q: "", tipoNeg: "", tipoImovel: "", tipologia: "", precoMin: "", precoMax: "", distrito: "", concelho: "", freguesia: "", conservacao: "", areaMin: "", estado: "", consultor: "" };

const eur = v => (v || v === 0) && v !== "" ? Number(v).toLocaleString("pt-PT", { maximumFractionDigits: 0 }) + " €" : "—";
const m2 = v => v ? `${Number(v).toLocaleString("pt-PT")} m²` : null;
const data = d => d ? new Date(d).toLocaleDateString("pt-PT") : "—";
const local = i => [i.freguesia, i.concelho].filter(Boolean).join(", ");
const lerLS = (k, d) => { try { return window.localStorage.getItem(k) || d; } catch (e) { return d; } };
const gravarLS = (k, v) => { try { window.localStorage.setItem(k, v); } catch (e) { /* ignora */ } };

// Colunas ordenáveis: como obter o valor de cada imóvel
const ORDENAR = {
  created_at: i => i.created_at || "",
  tipo_imovel: i => (i.tipo_imovel || "").toLowerCase(),
  tipologia: i => (i.tipologia || "").toLowerCase(),
  tipo_negocio: i => (i.tipo_negocio || "").toLowerCase(),
  preco: i => Number(i.preco) || 0,
  localizacao: i => [i.concelho, i.freguesia, i.morada].map(x => (x || "").toLowerCase()).join(" "),
  area_util: i => Number(i.area_util) || 0,
  ano_construcao: i => Number(i.ano_construcao) || 0,
  estado: i => i.estado || "",
  consultor_nome: i => (i.consultor_nome || "").toLowerCase(),
};

export function CatalogoImoveisEquipa({ supabase }) {
  const [imoveis, setImoveis] = useState(null);
  const [vista, setVista] = useState(() => lerLS("imoveis_equipa_vista", "cartoes"));
  const [f, setF] = useState(FILTROS_VAZIOS);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [ordem, setOrdem] = useState({ col: "created_at", dir: "desc" });
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    supabase.rpc("imoveis_equipa").then(({ data: d, error }) => {
      if (error) { alert("Erro a carregar imóveis: " + error.message); setImoveis([]); return; }
      setImoveis(d || []);
    });
  }, [supabase]);

  const setFiltro = (k, v) => { setF(p => ({ ...p, [k]: v })); setPagina(1); };
  const mudarVista = v => { setVista(v); gravarLS("imoveis_equipa_vista", v); };
  const ordenarPor = col => { setOrdem(o => o.col === col ? { col, dir: o.dir === "asc" ? "desc" : "asc" } : { col, dir: col === "created_at" ? "desc" : "asc" }); setPagina(1); };

  // Opções dos filtros tiradas dos próprios dados
  const opcoes = useMemo(() => {
    const uniq = k => [...new Set((imoveis || []).map(i => i[k]).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b), "pt"));
    return {
      tipoNeg: uniq("tipo_negocio"), tipoImovel: uniq("tipo_imovel"), tipologia: uniq("tipologia"),
      distrito: uniq("distrito"),
      concelho: [...new Set((imoveis || []).filter(i => !f.distrito || i.distrito === f.distrito).map(i => i.concelho).filter(Boolean))].sort((a, b) => a.localeCompare(b, "pt")),
      freguesia: [...new Set((imoveis || []).filter(i => (!f.distrito || i.distrito === f.distrito) && (!f.concelho || i.concelho === f.concelho)).map(i => i.freguesia).filter(Boolean))].sort((a, b) => a.localeCompare(b, "pt")),
      conservacao: uniq("estado_conservacao"), estado: uniq("estado"), consultor: uniq("consultor_nome"),
    };
  }, [imoveis, f.distrito, f.concelho]);

  const lista = useMemo(() => {
    if (!imoveis) return [];
    const q = f.q.trim().toLowerCase();
    const filtrados = imoveis.filter(i => {
      if (f.tipoNeg && i.tipo_negocio !== f.tipoNeg) return false;
      if (f.tipoImovel && i.tipo_imovel !== f.tipoImovel) return false;
      if (f.tipologia && i.tipologia !== f.tipologia) return false;
      if (f.precoMin && !(Number(i.preco) >= Number(f.precoMin))) return false;
      if (f.precoMax && !(Number(i.preco) <= Number(f.precoMax))) return false;
      if (f.distrito && i.distrito !== f.distrito) return false;
      if (f.concelho && i.concelho !== f.concelho) return false;
      if (f.freguesia && i.freguesia !== f.freguesia) return false;
      if (f.conservacao && i.estado_conservacao !== f.conservacao) return false;
      if (f.areaMin && !(Number(i.area_util) >= Number(f.areaMin))) return false;
      if (f.estado && i.estado !== f.estado) return false;
      if (f.consultor && i.consultor_nome !== f.consultor) return false;
      if (q && ![i.id_angariacao, i.tipo_imovel, i.tipologia, i.morada, i.concelho, i.freguesia, i.distrito, i.consultor_nome].some(v => (v || "").toLowerCase().includes(q))) return false;
      return true;
    });
    const val = ORDENAR[ordem.col] || ORDENAR.created_at;
    const mult = ordem.dir === "asc" ? 1 : -1;
    return [...filtrados].sort((a, b) => { const x = val(a), y = val(b); return x < y ? -mult : x > y ? mult : 0; });
  }, [imoveis, f, ordem]);

  if (!imoveis) return <div style={{ padding: 30, textAlign: "center", color: "#9ca3af" }}>A carregar imóveis...</div>;

  const nFiltros = Object.entries(f).filter(([k, v]) => k !== "q" && v !== "").length;
  const totalPaginas = Math.max(1, Math.ceil(lista.length / POR_PAGINA));
  const pag = Math.min(pagina, totalPaginas);
  const pagina_ = lista.slice((pag - 1) * POR_PAGINA, pag * POR_PAGINA);

  const INP = { padding: "8px 10px", border: "1.5px solid #ece9e3", borderRadius: 8, fontSize: 12.5, fontFamily: "inherit", background: "#fff", width: "100%" };
  // chamados como funções (não como componentes) para os campos não perderem o foco ao escrever
  const Sel = ({ k, label, ops, fmt = x => x }) => (
    <div key={k}><div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 3 }}>{label}</div>
      <select value={f[k]} onChange={e => setFiltro(k, e.target.value)} style={INP}>
        <option value="">Todos</option>{ops.map(o => <option key={o} value={o}>{fmt(o)}</option>)}
      </select></div>
  );
  const Num = ({ k, label, ph }) => (
    <div key={k}><div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 3 }}>{label}</div>
      <input type="number" min="0" value={f[k]} onChange={e => setFiltro(k, e.target.value)} placeholder={ph} style={INP} /></div>
  );
  const Seta = ({ col }) => ordem.col === col ? <span style={{ color: "#0B1B3D" }}>{ordem.dir === "asc" ? " ▲" : " ▼"}</span> : <span style={{ color: "#d1d5db" }}> ↕</span>;
  const TH = ({ col, children, alinhar = "left" }) => (
    <th onClick={col ? () => ordenarPor(col) : undefined} style={{ textAlign: alinhar, padding: "9px 8px", borderBottom: "2px solid #0B1B3D", fontSize: 11.5, whiteSpace: "nowrap", cursor: col ? "pointer" : "default", userSelect: "none", background: "#fff", position: "sticky", top: 0 }}>
      {children}{col && <Seta col={col} />}
    </th>
  );
  const TD = { padding: "8px", borderBottom: "1px solid #f3f4f6", fontSize: 12.5, whiteSpace: "nowrap" };
  const EstadoChip = ({ e }) => e ? <span style={{ fontSize: 10.5, fontWeight: 700, color: "#fff", background: COR_ESTADO[e] || "#6b7280", borderRadius: 20, padding: "2px 8px" }}>{ESTADOS[e] || e}</span> : null;
  const BTN = ativo => ({ background: ativo ? "#0B1B3D" : "#fff", color: ativo ? "#fff" : "#374151", border: "1.5px solid " + (ativo ? "#0B1B3D" : "#ece9e3"), borderRadius: 8, padding: "7px 12px", fontSize: 12.5, fontWeight: 600, cursor: "pointer" });

  return (
    <div>
      {/* Pesquisa, filtros, vista e ordenação */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
        <input value={f.q} onChange={e => setFiltro("q", e.target.value)} placeholder="Pesquisar referência, tipo, rua, localização, consultor..."
          style={{ ...INP, flex: 1, minWidth: 220, width: "auto", fontSize: 13, padding: "9px 12px" }} />
        <button onClick={() => setMostrarFiltros(v => !v)} style={BTN(mostrarFiltros || nFiltros > 0)}>⚙️ Filtros{nFiltros > 0 ? ` (${nFiltros})` : ""}</button>
        {vista === "cartoes" && (
          <select value={`${ordem.col}:${ordem.dir}`} onChange={e => { const [col, dir] = e.target.value.split(":"); setOrdem({ col, dir }); setPagina(1); }} style={{ ...INP, width: "auto" }}>
            <option value="created_at:desc">Mais recentes</option>
            <option value="created_at:asc">Mais antigos</option>
            <option value="preco:asc">Preço ▲</option>
            <option value="preco:desc">Preço ▼</option>
            <option value="tipo_imovel:asc">Tipo de imóvel</option>
            <option value="tipologia:asc">Tipologia</option>
            <option value="localizacao:asc">Localização</option>
            <option value="area_util:desc">Área útil ▼</option>
            <option value="ano_construcao:desc">Ano de construção ▼</option>
            <option value="consultor_nome:asc">Consultor</option>
          </select>
        )}
        <div style={{ display: "flex", gap: 4 }}>
          <button onClick={() => mudarVista("cartoes")} style={BTN(vista === "cartoes")}>▦ Cartões</button>
          <button onClick={() => mudarVista("lista")} style={BTN(vista === "lista")}>☰ Lista</button>
        </div>
      </div>

      {mostrarFiltros && (
        <div style={{ background: "#fff", border: "1px solid #ece9e3", borderRadius: 12, padding: 14, marginBottom: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
            {Sel({ k: "tipoNeg", label: "Tipo de negócio", ops: opcoes.tipoNeg })}
            {Sel({ k: "tipoImovel", label: "Tipo de imóvel", ops: opcoes.tipoImovel })}
            {Sel({ k: "tipologia", label: "Tipologia", ops: opcoes.tipologia })}
            {Num({ k: "precoMin", label: "Preço mínimo (€)", ph: "0" })}
            {Num({ k: "precoMax", label: "Preço máximo (€)", ph: "sem limite" })}
            {Sel({ k: "distrito", label: "Distrito", ops: opcoes.distrito })}
            {Sel({ k: "concelho", label: "Concelho", ops: opcoes.concelho })}
            {Sel({ k: "freguesia", label: "Freguesia", ops: opcoes.freguesia })}
            {Sel({ k: "conservacao", label: "Estado de conservação", ops: opcoes.conservacao })}
            {Num({ k: "areaMin", label: "Área útil mínima (m²)", ph: "0" })}
            {Sel({ k: "estado", label: "Estado da angariação", ops: opcoes.estado, fmt: e => ESTADOS[e] || e })}
            {Sel({ k: "consultor", label: "Consultor", ops: opcoes.consultor })}
          </div>
          {nFiltros > 0 && <button onClick={() => { setF(p => ({ ...FILTROS_VAZIOS, q: p.q })); setPagina(1); }} style={{ marginTop: 10, background: "none", border: "none", color: "#dc2626", fontSize: 12.5, fontWeight: 600, cursor: "pointer", padding: 0 }}>✕ Limpar filtros</button>}
        </div>
      )}

      <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 10 }}>
        {lista.length} imóve{lista.length === 1 ? "l" : "is"}{lista.length !== imoveis.length ? ` de ${imoveis.length}` : ""} · sem dados de proprietários, contactos ou documentos
      </div>

      {lista.length === 0 && (
        <div style={{ textAlign: "center", padding: 40, color: "#9ca3af", background: "#fff", borderRadius: 14, border: "1px solid #ece9e3" }}>Nenhum imóvel encontrado com estes filtros.</div>
      )}

      {lista.length > 0 && vista === "lista" && (
        <div style={{ overflowX: "auto", background: "#fff", borderRadius: 12, border: "1px solid #ece9e3" }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead><tr>
              <TH col="created_at">Inserida</TH>
              <TH>Ref.</TH>
              <TH col="tipo_imovel">Tipo</TH>
              <TH col="tipologia">Tipologia</TH>
              <TH col="tipo_negocio">Negócio</TH>
              <TH col="preco" alinhar="right">Preço</TH>
              <TH col="localizacao">Localização</TH>
              <TH col="area_util" alinhar="right">Área útil</TH>
              <TH col="ano_construcao">Ano</TH>
              <TH>Conservação</TH>
              <TH col="estado">Estado</TH>
              <TH col="consultor_nome">Consultor</TH>
              <TH>Anúncio</TH>
            </tr></thead>
            <tbody>
              {pagina_.map(i => (
                <tr key={i.id}>
                  <td style={TD}>{data(i.created_at)}</td>
                  <td style={{ ...TD, fontWeight: 600 }}>{i.id_angariacao || "—"}</td>
                  <td style={TD}>{i.tipo_imovel || "—"}</td>
                  <td style={TD}>{i.tipologia || "—"}</td>
                  <td style={TD}>{i.tipo_negocio || "—"}</td>
                  <td style={{ ...TD, textAlign: "right", fontWeight: 700 }}>{eur(i.preco)}</td>
                  <td style={{ ...TD, whiteSpace: "normal", minWidth: 180 }}>{local(i) || "—"}{i.morada && <div style={{ fontSize: 11, color: "#9ca3af" }}>{i.morada}</div>}</td>
                  <td style={{ ...TD, textAlign: "right" }}>{m2(i.area_util) || "—"}</td>
                  <td style={TD}>{i.ano_construcao || "—"}</td>
                  <td style={TD}>{i.estado_conservacao || "—"}</td>
                  <td style={TD}><EstadoChip e={i.estado} /></td>
                  <td style={TD}>{i.consultor_nome || "—"}</td>
                  <td style={TD}>{Array.isArray(i.links_portais) && i.links_portais[0] ? <a href={i.links_portais[0]} target="_blank" rel="noreferrer" style={{ color: "#2563eb", fontWeight: 600, textDecoration: "none" }}>Ver ↗</a> : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lista.length > 0 && vista === "cartoes" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
          {pagina_.map(i => {
            const areas = [["Útil", m2(i.area_util)], ["Bruta", m2(i.area_bruta)], ["Terreno", m2(i.area_terreno)]].filter(x => x[1]);
            return (
              <div key={i.id} style={{ background: "#fff", borderRadius: 12, border: "1px solid #ece9e3", padding: 16, opacity: i.estado === "rascunho" ? 0.85 : 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
                  <span style={{ fontWeight: 700, fontSize: 14 }}>{[i.tipo_imovel, i.tipologia].filter(Boolean).join(" ") || "Imóvel"}</span>
                  <span style={{ fontWeight: 800, fontSize: 14, color: "#0B1B3D" }}>{eur(i.preco)}</span>
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>{[i.morada, i.freguesia, i.concelho].filter(Boolean).join(" · ") || "Localização não indicada"}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                  <EstadoChip e={i.estado} />
                  {i.tipo_negocio && <Chip>{i.tipo_negocio}</Chip>}
                  {i.estado_conservacao && <Chip>{i.estado_conservacao}</Chip>}
                  {i.ano_construcao && <Chip>Ano {i.ano_construcao}</Chip>}
                  {areas.map(([l, v]) => <Chip key={l}>{l}: {v}</Chip>)}
                  {i.condominio && <Chip>Condomínio {i.valor_condominio ? eur(i.valor_condominio) + "/mês" : ""}</Chip>}
                  {i.certificado_energetico && <Chip>Cert. energético: {i.certificado_energetico}</Chip>}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11.5, color: "#9ca3af", gap: 8 }}>
                  <span>{i.id_angariacao || "—"} · {i.consultor_nome || "—"} · {data(i.created_at)}</span>
                  {Array.isArray(i.links_portais) && i.links_portais.length > 0 && (
                    <a href={i.links_portais[0]} target="_blank" rel="noreferrer" style={{ color: "#2563eb", fontWeight: 600, textDecoration: "none" }}>Ver anúncio ↗</a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {lista.length > POR_PAGINA && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 10, marginTop: 16 }}>
          <button onClick={() => setPagina(1)} disabled={pag === 1} style={{ ...BTN(false), opacity: pag === 1 ? 0.4 : 1 }} title="Primeira página">⏮</button>
          <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pag === 1} style={{ ...BTN(false), opacity: pag === 1 ? 0.4 : 1 }}>◀ Anterior</button>
          <span style={{ fontSize: 12.5, color: "#374151", fontWeight: 600 }}>Página {pag} de {totalPaginas}</span>
          <button onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))} disabled={pag === totalPaginas} style={{ ...BTN(false), opacity: pag === totalPaginas ? 0.4 : 1 }}>Seguinte ▶</button>
          <button onClick={() => setPagina(totalPaginas)} disabled={pag === totalPaginas} style={{ ...BTN(false), opacity: pag === totalPaginas ? 0.4 : 1 }} title="Última página">⏭</button>
        </div>
      )}
    </div>
  );
}

function Chip({ children }) {
  return <span style={{ fontSize: 11, background: "#f3f4f6", color: "#374151", borderRadius: 20, padding: "2px 9px", fontWeight: 600 }}>{children}</span>;
}
